// Types
import { ColumnWithOptions } from '@organisms/index';
import {
  DateRangePickerFilterProps,
  DebounceFilterInputProps,
  DropdownFilterProps,
  RangeFilterProps,
  RatingsButtonsFilterProps,
  UnitAndRangeFilterProps,
  YearPickerProps,
} from './Components';

/**
 * Enum representing the possible types of filter groups.
 */
export enum FilterGroupsType {
  /**
   * Type to use for filters that are not grouped.
   */
  SINGLE = 'SINGLE',
  /**
   * CII Rating filter group.
   */
  CII_RATING = 'CII_RATING',
}

/**
 * Represents the types of filters available.
 */
export enum FilterType {
  /**
   * Debounce Input Filter Type.
   * Represents a debounce input filter.
   */
  DEBOUNCE_INPUT = 'DebounceInput',
  /**
   * Ratings Buttons Filter Type.
   * Represents a ratings buttons filter.
   */
  RATINGS_BUTTONS_FILTER = 'RatingsButtonsFilter',
  /**
   * Range Filter Type.
   * Represents a range of numbers filter.
   */
  RANGE_FILTER = 'RangeFilter',
  /**
   * Unit And Range Filter Type.
   * Represents a unit and a range of numbers filter.
   */
  UNIT_AND_RANGE_FILTER = 'UnitAndRange',
  /**
   * Date range picker.
   * Represents a range of dates filter.
   */
  DATE_RANGE_PICKER = 'DateRangePicker',
  /**
   * Year range picker.
   * Represents a year filter.
   */
  YEAR_PICKER = 'YearPicker',
  /**
   * Dropdown filter type.
   * Represents a dropdown filter.
   */
  DROPDOWN_FILTER = 'DROPDOWN_FILTER',
}

/**
 * An enumeration to represent the visibility state of a filter.
 */
export enum FilterVariant {
  /**
   * The state of the filter when it is visible.
   */
  VISIBLE = 'VISIBLE',

  /**
   * The state of the filter when it is hidden.
   */
  HIDDEN = 'HIDDEN',
}

/**
 * Represents the options for different filter types in the ElementOptions.
 */
export type ElementOptions = {
  /**
   * Debounce Input Filter Options.
   * Specifies the props for the DebounceFilterInput component.
   */
  [FilterType.DEBOUNCE_INPUT]: DebounceFilterInputProps;
  /**
   * Ratings Buttons Filter Options.
   * Specifies an empty record as no specific props are needed for this filter type.
   */
  [FilterType.RATINGS_BUTTONS_FILTER]: RatingsButtonsFilterProps;
  /**
   * Range Filter Options.
   * Specifies the props for the RangeFilter component.
   */
  [FilterType.RANGE_FILTER]: RangeFilterProps;
  /**
   * Unit And Range Filter Options.
   * Specifies the props for the UnitAndRangeFilter component.
   */
  [FilterType.UNIT_AND_RANGE_FILTER]: UnitAndRangeFilterProps;
  /**
   * Date Range Filter Options.
   * Specifies the props for the DateRangePickerFilter component.
   */
  [FilterType.DATE_RANGE_PICKER]: DateRangePickerFilterProps;
  /**
   * Year Filter Options.
   * Specifies the props for the YearPicker component.
   */
  [FilterType.YEAR_PICKER]: YearPickerProps;
  /**
   * Dropdown Filter Options.
   * Specifies the props for the DropdownFilter component.
   */
  [FilterType.DROPDOWN_FILTER]: DropdownFilterProps;
};

/**
 * Represents the configuration for props based on the filter type.
 * @template Type - The type containing filter configurations.
 * @template FilterKey - The key of the filter configuration within the Type.
 */
type PropsConfig<Type, FilterKey extends keyof Type> = Type[FilterKey] extends undefined
  ? { props?: Record<string, never> } // If the filter configuration is undefined, no props are required.
  : { props: Type[FilterKey] }; // If the filter configuration is defined, props of that type are required.

/**
 * Represents the configuration for a filter.
 * @template FilterKey - The type of the filter.
 */
type FilterConfig<FilterKey> = {
  /**
   * The filter key.
   */
  filter: FilterKey;
  /**
   * The column Id.
   */
  columnId: string;
  /**
   * Determines if the filter is hidden on small views.
   */
  hidden: boolean;
  /**
   * Determines if the filter belongs to a subgroup.
   */
  groupId: FilterGroupsType;
};

/**
 * Represents the configuration for multiple filters.
 * @template Type - The type containing filter configurations.
 * @template Key - The key type to iterate over the filter configurations within the Type.
 */
type FiltersConfig<Type, Key extends keyof Type = keyof Type> = Key extends keyof Type
  ? FilterConfig<Key> & PropsConfig<Type, Key> // If Key is a valid key within Type, combine FilterConfig and PropsConfig.
  : never; // Otherwise, return 'never' type.

/**
 * Represents a filter option configuration based on the available element options.
 */
export type FilterOption = FiltersConfig<ElementOptions>;

/**
 * Represents a mapping of column names to their respective filter option configurations.
 * @template ColumnNames - The type of column names.
 */
export type FilterOptionsMap<ColumnNames extends string> = Partial<Record<ColumnNames, FilterOption>>;

/**
 * Represents the properties required for the filter factory.
 * @template TableData - The type of data in the table.
 * @template ColumnNames - The type of column names.
 */
export type FilterFactoryProps<TableData, ColumnNames extends string> = {
  /**
   * The ID of the column.
   */
  columnId: string;
  /**
   * The column configuration.
   */
  column: ColumnWithOptions<TableData>;
  /**
   * The mapping of column names to their respective filter option configurations.
   */
  filterOptionsMap: FilterOptionsMap<ColumnNames>;
  /**
   * Visibility state of a filter
   */
  variant?: FilterVariant;
  /**
   * Flag to determine if the filters are going to be displayed in smaller views
   */
  smallerViews?: boolean;
};

/**
 * Represents the properties for a column filter.
 * @template TableData - The type of data in the table.
 */
export type ColumnFilterProps<TableData, FilterProps> = {
  /**
   * The column configuration.
   */
  column: ColumnWithOptions<TableData>;
  /**
   * The props specific to the filter component.
   */
  props: FilterProps;
  /**
   * Visibility state of a filter
   */
  variant?: FilterVariant;
  /**
   * Flag to determine if the filters are going to be displayed in smaller views
   */
  smallerViews?: boolean;
};
